/* eslint no-prototype-builtins: "off" */

'use strict';

const jQuery = require('jquery');

const mediator = require('./mediator');
const Request = require('./request');
const Response = require('./response');
let requestHandler;

let baseUrl = '';

/**
 * Request handler create a call to the api with several functions:
 *
 * That build a response and request object for retrieve data
 *
 * @module core/requestHandler
 * @requires jquery
 * @requires core/mediator
 * @requires core/request
 * @requires core/response
 * @author Florian Kroockmann <florian.kroockmann@lp-digital.fr>
 */
module.exports = requestHandler = {
  /**
   * Create a POST request
   *
   * @function create
   * @example RequestHandler.create('classcontent/Text/Paragraph/c9bf609ccdf0f3d5c63a542fd26632f3', data);
   * @param  {string} type
   * @param  {object} data
   * @param  {object} [criteria] criteria
   * @param  {object} [orderBy] orderBy
   * @param  {Integer} [start] start
   * @param  {Integer} [limit] limit
   * @returns {Promise} data
   */
  create: function(type, data, criteria, orderBy, start, limit) {
    return handle('post', type, formatData(data, criteria, orderBy, start, limit));
  },

  /**
   * Create DELETE request
   *
   * @function del
   * @example RequestHandler.del('classcontent/Text/Paragraph/c9bf609ccdf0f3d5c63a542fd26632f3', data);
   * @param  {string} type
   * @param  {object} [criteria] criteria
   * @param  {object} [orderBy] orderBy
   * @param  {Integer} [start] start
   * @param  {Integer} [limit] limit
   * @returns {Promise} data
   */
  del: function(type, criteria, orderBy, start, limit) {
    return handle('delete', type, formatData({}, criteria, orderBy, start, limit));
  },

  /**
   * Create GET request
   *
   * @function read
   * @example RequestHandler.read('pages', {'title': 'preview'});
   * @param  {string} type
   * @param  {object} [criteria] criteria
   * @param  {object} [orderBy] orderBy
   * @param  {Integer} [start] start
   * @param  {Integer} [limit] limit
   * @returns {Promise} data
   */
  read: function(type, criteria, orderBy, start, limit) {
    return handle('get', type, formatData({}, criteria, orderBy, start, limit));
  },

  /**
   * Create PUT request
   *
   * @function update
   * @example RequestHandler.update('classcontent/Text/Paragraph/c9bf609ccdf0f3d5c63a542fd26632f3', data);
   * @param  {string} type
   * @param  {object} data
   * @param  {object} [criteria] criteria
   * @param  {object} [orderBy] orderBy
   * @param  {Integer} [start] start
   * @param  {Integer} [limit] limit
   * @returns {Promise} data
   */
  update: function(type, data, criteria, orderBy, start, limit) {
    return handle('put', type, formatData(data, criteria, orderBy, start, limit));
  },

  /**
   * Create LINK request
   *
   * @function link
   * @example RequestHandler.link('classcontent/Text/Paragraph/c9bf609ccdf0f3d5c63a542fd26632f3', data);
   * @param  {string} type
   * @param  {object} data
   * @param  {object} [criteria] criteria
   * @param  {object} [orderBy] orderBy
   * @param  {Integer} [start] start
   * @param  {Integer} [limit] limit
   * @returns {Promise} data
   */
  link: function(type, data, criteria, orderBy, start, limit) {
    return handle('link', type, formatData(data, criteria, orderBy, start, limit));
  },

  /**
   * Create PATCH request
   *
   * @function patch
   * @example RequestHandler.patch('classcontent/Text/Paragraph/c9bf609ccdf0f3d5c63a542fd26632f3', data);
   * @param  {string} type
   * @param  {object} data
   * @param  {object} criteria
   * @param  {object} orderBy
   * @param  {Integer} start
   * @param  {Integer} limit
   * @returns {Promise} data
   */
  patch: function(type, data, criteria, orderBy, start, limit) {
    return handle('patch', type, formatData(data, criteria, orderBy, start, limit));
  },

  /**
   * Set the base url of the api
   *
   * @function setBaseUrl
   * @example request.setBaseUrl('localhost');
   * @param {string} url
   * @returns {requestHandler} this
   */
  setBaseUrl: function(url) {
    baseUrl = url;

    return this;
  },

  /**
   * Return the current base url
   *
   * @function getBaseUrl
   * @example request.getBaseUrl();
   * @returns {string} base url
   */
  getBaseUrl: function() {
    return baseUrl;
  },

  /**
   * Send the request to the api
   *
   * @function send
   * @example request.send(request);
   * @param  {Request} request
   * @returns {*} response
   */
  send: function(request) {
    let dfd = jQuery.Deferred();
    let config = {};

    if (request instanceof Request) {
      mediator.publish('request:send:before', request);

      config = {
        url: encodeURI(request.getUrl()),
        type: request.getMethod(),
        data: request.getData(),
        headers: request.getHeaders(),
      };

      jQuery.ajax(config)
        .done((data, statusText, xhr) => {
          let response = buildResponse(xhr, statusText, data);

          mediator.publish('request:send:done', response);

          dfd.resolve(response.getData(), response, request);
        })
        .fail((xhr, statusText, errorThrown) => {
          let response = buildResponse(xhr, statusText, '', errorThrown);

          mediator.publish('request:send:fail', {
            request,
            response,
          });

          dfd.reject(response, request);
        });
    } else {
      throw new Error('The parameter must be an instance of Request');
    }

    return dfd.promise();
  },

  /**
   * Add search to the url
   *
   * @function addSearch
   * @example request.addSearch('/api', 'country', 'france')
   * @param {string} url
   * @param {string} key
   * @param {string} value
   * @returns {string} url
   */
  addSearch: function(url, key, value) {
    return url + ((url.indexOf('?') !== -1) ? '&' : '?') + (key + '=' + value);
  },
};

/**
 * Build response from the xfr object
 *
 * @ignore
 * @param  {object} xhr
 * @param  {string} statusText
 * @param  {object} data
 * @param  {string} errorThrown
 * @returns {Response} response
 */
const buildResponse = function(xhr, statusText, data, errorThrown) {
  let response = new Response();

  buildHeaders(xhr.getAllResponseHeaders(), response);

  if (data) {
    response.setData(data);
  }

  response.setRawData(xhr.responseText);
  response.setStatus(xhr.status);
  response.setStatusText(statusText);

  if (errorThrown) {
    response.setErrorText(errorThrown);
  }

  return response;
};

/**
 * Build the data from request
 *
 * @ignore
 * @param  {object} data
 * @param  {object} criteria
 * @param  {object} orderBy
 * @param  {Integer} start
 * @param  {Integer} limit
 * @returns {object} data
 */
const formatData = function(data, criteria, orderBy, start, limit) {
  return {
    data: data,
    criteria: criteria || null,
    orderBy: orderBy || {},
    start: start || 0,
    limit: limit || null,
  };
};

/**
 * Builds headers to the response
 *
 * @ignore
 * @param  {string} headers
 * @param  {Response} response
 */
const buildHeaders = function(headers, response) {
  headers.split('\r').forEach((header) => {
    let identifierPos = header.indexOf(':');

    if (-1 !== identifierPos) {
      let name = header.substring(0, identifierPos).trim();
      let value = header.substring(identifierPos + 1).trim();

      response.addHeader(name, value);
    }
  });
};

/**
 * Handle the request
 *
 * @ignore
 * @param  {string} method
 * @param  {string} type
 * @param  {object} data
 * @returns {Promise} data
 */
const handle = function(method, type, data) {
  let url = baseUrl;
  let range;
  let request = new Request();

  request.headers = {};
  request.setContentType('application/json');
  request.addHeader('Accept', 'application/json');

  url = url + '/' + type;

  if ('get' === method) {
    request.setMethod('GET');
    url = computeCriteria(url, data);
  } else if ('put' === method || 'patch' === method || 'link' === method || 'delete' === method) {
    request.setMethod('update' === method ? 'put' : method);
    url = computeCriteria(url, data);

    if (data.hasOwnProperty('data')) {
      if ('patch' === method) {
        request.setData(computePatchOperations(data.data));
      } else {
        request.setData(data.data);
      }
    }
  } else if ('post' === method) {
    request.setMethod('POST');
    url = computeCriteria(url, data);

    if (data.hasOwnProperty('data')) {
      request.setData(data.data);
    }
  }

  if (data.hasOwnProperty('limit') && null !== data.limit) {
    range = (data.hasOwnProperty('start') ? data.start : '0') + ',' + data.limit;
    request.addHeader('Range', range);
  }

  url = computeOrderBy(url, data);

  request.setUrl(url);

  if (null !== request.getData()) {
    request.setData(JSON.stringify(request.getData()));
  }

  mediator.publish('rest:send:before', request);

  return requestHandler.send(request);
};

/**
 * Compute order by
 *
 * @ignore
 * @param  {string} url
 * @param  {object} data
 * @returns {string} url
 */
const computeOrderBy = function(url, data) {
  if (typeof data.orderBy === 'object') {
    for (let key in data.orderBy) {
      if (data.orderBy.hasOwnProperty(key)) {
        let order = data.orderBy[key];

        url = requestHandler.addSearch(url, 'order_by[' + key + ']', order);
      }
    }
  }

  return url;
};

/**
 * Compute criteria
 *
 * @ignore
 * @param  {string} url
 * @param  {object} data
 * @returns {string} url
 */
const computeCriteria = function(url, data) {
  let criteria = data.criteria || null;

  if (null === criteria) {
    return url;
  }

  for (let key in criteria) {
    if (criteria.hasOwnProperty(key)) {
      let criterion = criteria[key];

      if ('uid' === key || 'id' === key) {
        url = url + ((url[url.length - 1] === '/') ? '' : '/') + criterion.toString();
      } else {
        url = requestHandler.addSearch(url, key, criterion);
      }
    }
  }

  return url;
};

/**
 * Compute patch operations
 *
 * @ignore
 * @param  {object} data
 * @returns {Array} operations
 */
const computePatchOperations = function(data) {
  let operations = [];

  for (let key in data) {
    if (data.hasOwnProperty(key)) {
      let value = data[key];

      operations.push({
        op: 'replace',
        path: '/' + key,
        value: value,
      });
    }
  }

  return operations;
};
